import { Icon } from '@buggy/shared'
import { Button, Flex, Form } from 'antd'
import { groupBy, uniq } from 'lodash-es'
import type { FunctionComponent } from 'react'
import styled from 'styled-components'
import CollaborativeItemInput from '@/components/common/collaborative/collaborative-item-input'
import CollaborativeItemTypeCascader, {
  type CollaborativeItemTypeCascaderProps,
} from '@/components/common/collaborative/collaborative-item-type-cascader'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { Optional } from '@/types/optional'
import type { CollaborativeItem } from '@/types/schema/collaborative/collaborative-item'
import { PlatformShortcode } from '@/utils/convert-platform'

const MAXIMUM_COLLABORATIVE_PLATFORM_LENGTH = 20
export const MAXIMUM_COLLABORATIVE_ITEM_COUNT = 50
const MAXIMUM_COLLABORATIVE_ITEM_LENGTH = 20

interface CollaborativeItemFormListProps {
  name: string
  totalFee?: string
  initialValue?: CollaborativeItem[]
  disabledItemTypeCascaderOptions?: CollaborativeItemTypeCascaderProps['disabledOptions']
  maxItemCount?: number
}

const CollaborativeItemFormList: FunctionComponent<
  CollaborativeItemFormListProps
> = ({
  name,
  disabledItemTypeCascaderOptions,
  totalFee,
  initialValue = [
    {
      platform_name: PlatformShortcode.Instagram,
      is_custom_platform: false,
      is_custom_collaborative_item: false,
      collaborative_item: 'post',
      cooperation_fee: 0,
      collaborative_items_count: 1,
    },
  ],
  maxItemCount = MAXIMUM_COLLABORATIVE_ITEM_COUNT,
}) => {
  const { formatMessage } = useIntl()

  return (
    <Form.List
      initialValue={initialValue}
      name={name}
      rules={[
        {
          validator: (
            _rules,
            items: Optional<CollaborativeItem[]>,
          ): Promise<void> => {
            if (!items?.length) {
              return Promise.resolve()
            }

            const nonEmptyCustomPlatforms = items.filter(
              (item) => item.is_custom_platform && item.platform_name,
            )

            if (nonEmptyCustomPlatforms.length) {
              const uniqueCustomPlatformNames = uniq(
                nonEmptyCustomPlatforms.map((item) => item.platform_name),
              )

              if (
                nonEmptyCustomPlatforms.length !==
                uniqueCustomPlatformNames.length
              ) {
                return Promise.reject(
                  new Error(
                    formatMessage(
                      { id: 'general:duplicated_add' },
                      {
                        resource: formatMessage({
                          id: 'general:column_title_collaboration_platform',
                        }),
                      },
                    ),
                  ),
                )
              }
            }

            const containsDuplicatedItems = Object.values(
              groupBy(items, 'platform_name'),
            ).some((groupedItems) => {
              const nonEmptyCustomItems = groupedItems.filter(
                (item) =>
                  item.is_custom_collaborative_item && item.collaborative_item,
              )

              const uniqueCollaborativeItems = uniq(
                nonEmptyCustomItems.map((item) => item.collaborative_item),
              )

              return (
                nonEmptyCustomItems.length !== uniqueCollaborativeItems.length
              )
            })

            if (containsDuplicatedItems) {
              return Promise.reject(
                new Error(
                  formatMessage(
                    { id: 'general:duplicated_add' },
                    {
                      resource: formatMessage({
                        id: 'general:column_title_collaboration_item_type',
                      }),
                    },
                  ),
                ),
              )
            }

            return Promise.resolve()
          },
        },
      ]}
    >
      {(fields, { add, remove }, { errors }) => {
        return (
          <>
            <InputList>
              {fields.map((field, index) => {
                return (
                  <StyledFormItem
                    {...field}
                    key={field.key}
                    rules={[
                      {
                        validator: (
                          _rules,
                          item: Partial<CollaborativeItem> | undefined,
                        ): Promise<void> => {
                          if (item?.is_custom_platform) {
                            if (!item.platform_name?.length) {
                              return Promise.reject(
                                new Error(
                                  formatMessage(
                                    { id: 'error:please_fulfill' },
                                    {
                                      inputField: formatMessage({
                                        id: 'general:column_title_collaboration_platform',
                                      }),
                                    },
                                  ),
                                ),
                              )
                            }

                            if (
                              item.platform_name.length >
                              MAXIMUM_COLLABORATIVE_PLATFORM_LENGTH
                            ) {
                              return Promise.reject(
                                new Error(
                                  formatMessage(
                                    { id: 'error:input_filed_maximum_limit' },
                                    {
                                      inputField: formatMessage({
                                        id: 'general:column_title_collaboration_platform',
                                      }),
                                      limit:
                                        MAXIMUM_COLLABORATIVE_PLATFORM_LENGTH,
                                    },
                                  ),
                                ),
                              )
                            }
                          }

                          if (item?.is_custom_collaborative_item) {
                            if (!item.collaborative_item?.length) {
                              return Promise.reject(
                                new Error(
                                  formatMessage(
                                    { id: 'error:please_fulfill' },
                                    {
                                      inputField: formatMessage({
                                        id: 'general:column_title_collaboration_item_type',
                                      }),
                                    },
                                  ),
                                ),
                              )
                            }

                            if (
                              item.collaborative_item.length >
                              MAXIMUM_COLLABORATIVE_ITEM_LENGTH
                            ) {
                              return Promise.reject(
                                new Error(
                                  formatMessage(
                                    { id: 'error:input_filed_maximum_limit' },
                                    {
                                      inputField: formatMessage({
                                        id: 'general:column_title_collaboration_item_type',
                                      }),
                                      limit: MAXIMUM_COLLABORATIVE_ITEM_LENGTH,
                                    },
                                  ),
                                ),
                              )
                            }
                          }

                          return Promise.resolve()
                        },
                      },
                    ]}
                    validateTrigger={[]}
                  >
                    <CollaborativeItemInput
                      disabledItemTypeCascaderOptions={
                        disabledItemTypeCascaderOptions
                      }
                      onDelete={
                        fields.length === 1
                          ? undefined
                          : (): void => {
                              remove(index)
                            }
                      }
                    />
                  </StyledFormItem>
                )
              })}
            </InputList>
            <Form.ErrorList errors={errors} />
            <Flex justify='space-between'>
              <CollaborativeItemTypeCascader
                disabledOptions={disabledItemTypeCascaderOptions}
                onChange={([platform, itemType]) => {
                  if (platform === PlatformShortcode.Other) {
                    add({
                      collaborative_items_count: 1,
                      cooperation_fee: 0,
                      platform_name: '',
                      is_custom_platform: true,
                      collaborative_item: '',
                      is_custom_collaborative_item: true,
                    })
                  } else {
                    add({
                      platform_name: platform,
                      collaborative_items_count: 1,
                      cooperation_fee: 0,
                      is_custom_platform: false,
                      ...(itemType === 'custom'
                        ? {
                            collaborative_item: '',
                            is_custom_collaborative_item: true,
                          }
                        : {
                            collaborative_item: itemType,
                            is_custom_collaborative_item: false,
                          }),
                    })
                  }
                }}
              >
                <AddButton
                  ghost
                  disabled={fields.length >= maxItemCount}
                  icon={<Icon name='plus' />}
                  size='small'
                  type='link'
                >
                  {formatMessage({ id: 'campaign:cooperation_add_act' })}
                </AddButton>
              </CollaborativeItemTypeCascader>
              <TotalFee>{totalFee}</TotalFee>
            </Flex>
          </>
        )
      }}
    </Form.List>
  )
}

const InputList = styled.div`
  max-height: 50vh;
  overflow-y: auto;
`

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 16px;
`

const AddButton = styled(Button)`
  padding: 0;
  height: 24px;
  line-height: 24px;
`

const TotalFee = styled.div`
  overflow: hidden;
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`

export default CollaborativeItemFormList
